import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Select,
  Statistic,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Descriptions,
  Image,
  Divider,
  Alert,
} from 'antd';
import {
  ReloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import reportService, { Report, ReportFilters, ReportStats } from '../services/reportService';
import { getImageUrl } from '../config/api';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const Reports: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<Report[]>([]);
  const [stats, setStats] = useState<ReportStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<ReportFilters>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const [pagination, setPagination] = useState<any>(null);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [actionForm] = Form.useForm();

  useEffect(() => {
    loadReports();
    loadStats();
  }, [filters]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const data = await reportService.getReports(filters);
      setReports(data.reports);
      setPagination(data.pagination);
    } catch (error: any) {
      message.error(error.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await reportService.getReportStats();
      setStats(data);
    } catch (error: any) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleStatusChange = async (id: string, status: string) => {
    try {
      await reportService.updateReportStatus(id, status as any);
      message.success('Report status updated');
      loadReports();
      loadStats();
    } catch (error: any) {
      message.error(error.message || 'Failed to update status');
    }
  };

  const handleViewDetail = async (id: string) => {
    try {
      const report = await reportService.getReportById(id);
      setSelectedReport(report);
      setDetailModalVisible(true);
    } catch (error: any) {
      message.error(error.message || 'Failed to load report details');
    }
  };

  const handleTakeAction = (report: Report) => {
    setSelectedReport(report);
    actionForm.resetFields();
    actionForm.setFieldsValue({
      action: 'pending',
      actionNotes: '',
    });
    setActionModalVisible(true);
  };

  const handleActionSubmit = async (values: any) => {
    if (!selectedReport) return;
    try {
      await reportService.takeAction(
        selectedReport._id,
        values.action,
        values.actionNotes,
        selectedReport.reportedUser?._id,
        selectedReport.reportedPost?._id
      );
      message.success('Action taken successfully');
      setActionModalVisible(false);
      actionForm.resetFields();
      loadReports();
      loadStats();
    } catch (error: any) {
      message.error(error.message || 'Failed to take action');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'orange';
      case 'reviewing':
        return 'blue';
      case 'resolved':
        return 'green';
      case 'dismissed':
        return 'default';
      case 'escalated':
        return 'red';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'animal_cruelty':
      case 'scam':
        return 'red';
      case 'abuse':
      case 'fake_listing':
        return 'orange';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'red';
      case 'high':
        return 'orange';
      case 'normal':
        return 'blue';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: '_id',
      key: '_id',
      width: 100,
      render: (id: string) => <Text copyable={{ text: id }} style={{ fontSize: '12px' }}>{id.substring(0, 8)}...</Text>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>
          {type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
        </Tag>
      ),
    },
    {
      title: 'Reporter',
      dataIndex: 'reporter',
      key: 'reporter',
      width: 150,
      render: (reporter: Report['reporter']) => (
        <div>
          <Text strong>{reporter.name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>@{reporter.username}</Text>
        </div>
      ),
    },
    {
      title: 'Reported',
      key: 'reported',
      width: 150,
      render: (_: any, record: Report) => {
        if (record.reportedUser) {
          return (
            <div>
              <Tag color="purple">User</Tag>
              <br />
              <Text style={{ fontSize: '12px' }}>{record.reportedUser.name}</Text>
            </div>
          );
        }
        if (record.reportedPost) {
          return (
            <div>
              <Tag color="blue">Post</Tag>
              <br />
              <Text style={{ fontSize: '12px' }} ellipsis>
                {record.reportedPost.caption || 'No caption'}
              </Text>
            </div>
          );
        }
        if (record.reportedPet) {
          return (
            <div>
              <Tag color="green">Pet</Tag>
              <br />
              <Text style={{ fontSize: '12px' }}>{record.reportedPet.title || 'Pet Listing'}</Text>
            </div>
          );
        }
        return '-';
      },
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      render: (reason: string) => <Text ellipsis={{ tooltip: reason }}>{reason}</Text>,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority: string) => (
        <Tag color={getPriorityColor(priority)}>{priority.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'actionTaken',
      key: 'actionTaken',
      width: 120,
      render: (action: string) => {
        if (action === 'pending') return <Tag>Pending</Tag>;
        return <Tag color="green">{action.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}</Tag>;
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: Report) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record._id)}
            size="small"
          >
            View
          </Button>
          {record.status === 'pending' && (
            <Button
              type="link"
              onClick={() => handleTakeAction(record)}
              size="small"
            >
              Take Action
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Reports & Abuse Handling</Title>
      <Text type="secondary">Manage user reports and take appropriate actions</Text>

      <Divider />

      {/* Statistics */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Pending Reports"
                value={stats.pendingCount}
                valueStyle={{ color: '#faad14' }}
                prefix={<WarningOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Urgent Reports"
                value={stats.urgentCount}
                valueStyle={{ color: '#cf1322' }}
                prefix={<ExclamationCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Resolved"
                value={stats.statusStats.find((s) => s._id === 'resolved')?.count || 0}
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Reports"
                value={stats.statusStats.reduce((sum, s) => sum + s.count, 0)}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by Status"
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value, page: 1 })}
              allowClear
            >
              <Option value="pending">Pending</Option>
              <Option value="reviewing">Reviewing</Option>
              <Option value="resolved">Resolved</Option>
              <Option value="dismissed">Dismissed</Option>
              <Option value="escalated">Escalated</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by Type"
              style={{ width: '100%' }}
              value={filters.type}
              onChange={(value) => setFilters({ ...filters, type: value, page: 1 })}
              allowClear
            >
              <Option value="fake_listing">Fake Listing</Option>
              <Option value="abuse">Abuse</Option>
              <Option value="scam">Scam</Option>
              <Option value="animal_cruelty">Animal Cruelty</Option>
              <Option value="spam">Spam</Option>
              <Option value="inappropriate_content">Inappropriate Content</Option>
              <Option value="other">Other</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by Priority"
              style={{ width: '100%' }}
              value={filters.priority}
              onChange={(value) => setFilters({ ...filters, priority: value, page: 1 })}
              allowClear
            >
              <Option value="urgent">Urgent</Option>
              <Option value="high">High</Option>
              <Option value="normal">Normal</Option>
              <Option value="low">Low</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Button icon={<ReloadOutlined />} onClick={loadReports}>
              Refresh
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={reports}
          rowKey="_id"
          loading={loading}
          scroll={{ x: 1500 }}
          pagination={{
            current: pagination?.page || 1,
            pageSize: pagination?.limit || 20,
            total: pagination?.total || 0,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} reports`,
            onChange: (page, pageSize) => {
              setFilters({ ...filters, page, limit: pageSize });
            },
          }}
        />
      </Card>

      {/* Report Detail Modal */}
      <Modal
        title="Report Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
          selectedReport?.status === 'pending' && (
            <Button
              key="action"
              type="primary"
              onClick={() => {
                setDetailModalVisible(false);
                handleTakeAction(selectedReport!);
              }}
            >
              Take Action
            </Button>
          ),
        ]}
        width={800}
      >
        {selectedReport && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Report ID" span={2}>
                <Text copyable>{selectedReport._id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                <Tag color={getTypeColor(selectedReport.type)}>
                  {selectedReport.type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Priority">
                <Tag color={getPriorityColor(selectedReport.priority)}>
                  {selectedReport.priority.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(selectedReport.status)}>
                  {selectedReport.status.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Action Taken">
                {selectedReport.actionTaken ? (
                  <Tag color="green">
                    {selectedReport.actionTaken.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  </Tag>
                ) : (
                  <Tag>Pending</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Reporter" span={2}>
                <Space>
                  {selectedReport.reporter.profileImage && (
                    <Image
                      src={getImageUrl(selectedReport.reporter.profileImage)}
                      width={40}
                      height={40}
                      style={{ borderRadius: '50%' }}
                    />
                  )}
                  <div>
                    <Text strong>{selectedReport.reporter.name}</Text>
                    <br />
                    <Text type="secondary">@{selectedReport.reporter.username}</Text>
                  </div>
                </Space>
              </Descriptions.Item>
              {selectedReport.reportedUser && (
                <Descriptions.Item label="Reported User" span={2}>
                  <Space>
                    {selectedReport.reportedUser.profileImage && (
                      <Image
                        src={getImageUrl(selectedReport.reportedUser.profileImage)}
                        width={40}
                        height={40}
                        style={{ borderRadius: '50%' }}
                      />
                    )}
                    <div>
                      <Text strong>{selectedReport.reportedUser.name}</Text>
                      <br />
                      <Text type="secondary">@{selectedReport.reportedUser.username}</Text>
                      <br />
                      <Button
                        type="link"
                        size="small"
                        onClick={() => navigate(`/users/${selectedReport.reportedUser!._id}`)}
                      >
                        View Profile
                      </Button>
                    </div>
                  </Space>
                </Descriptions.Item>
              )}
              {selectedReport.reportedPost && (
                <Descriptions.Item label="Reported Post" span={2}>
                  <div>
                    <Text>{selectedReport.reportedPost.caption || 'No caption'}</Text>
                    <br />
                    <Button
                      type="link"
                      size="small"
                      onClick={() => navigate(`/posts/${selectedReport.reportedPost!._id}`)}
                    >
                      View Post
                    </Button>
                  </div>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Reason" span={2}>
                {selectedReport.reason}
              </Descriptions.Item>
              {selectedReport.description && (
                <Descriptions.Item label="Description" span={2}>
                  {selectedReport.description}
                </Descriptions.Item>
              )}
              {selectedReport.actionNotes && (
                <Descriptions.Item label="Action Notes" span={2}>
                  {selectedReport.actionNotes}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Created At">
                {new Date(selectedReport.createdAt).toLocaleString()}
              </Descriptions.Item>
              {selectedReport.resolvedAt && (
                <Descriptions.Item label="Resolved At">
                  {new Date(selectedReport.resolvedAt).toLocaleString()}
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* Take Action Modal */}
      <Modal
        title="Take Action on Report"
        open={actionModalVisible}
        onOk={() => actionForm.submit()}
        onCancel={() => {
          setActionModalVisible(false);
          actionForm.resetFields();
        }}
        width={600}
      >
        <Form
          form={actionForm}
          layout="vertical"
          onFinish={handleActionSubmit}
        >
          <Form.Item
            name="action"
            label="Action"
            rules={[{ required: true, message: 'Please select an action' }]}
          >
            <Select>
              <Option value="no_action">No Action</Option>
              <Option value="post_deleted">Delete Post</Option>
              <Option value="user_warned">Warn User</Option>
              <Option value="temporary_ban">Temporary Ban (7 days)</Option>
              <Option value="permanent_ban">Permanent Ban</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="actionNotes"
            label="Action Notes"
            tooltip="Internal notes about the action taken"
          >
            <TextArea rows={4} placeholder="Enter notes about the action taken..." />
          </Form.Item>

          {selectedReport && (
            <Alert
              message="Report Information"
              description={
                <div>
                  <Text strong>Type:</Text> {selectedReport.type.replace('_', ' ')}
                  <br />
                  <Text strong>Priority:</Text> {selectedReport.priority}
                  {selectedReport.reportedUser && (
                    <>
                      <br />
                      <Text strong>Reported User:</Text> {selectedReport.reportedUser.name}
                    </>
                  )}
                  {selectedReport.reportedPost && (
                    <>
                      <br />
                      <Text strong>Reported Post:</Text> {selectedReport.reportedPost.caption?.substring(0, 50) || 'No caption'}
                    </>
                  )}
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default Reports;

